// Session token storage. localStorage is a per-browser convenience; the
// server is the authority and every read of it is guarded.
import { useSyncExternalStore } from 'react'
import { queryClient } from './queryClient'

const KEY = 'wattool.session'
const listeners = new Set<() => void>()

export function readToken(): string | null {
  try {
    return localStorage.getItem(KEY)
  } catch {
    return null
  }
}

export function storeToken(token: string) {
  try {
    localStorage.setItem(KEY, token)
  } catch {
    // Private mode or blocked storage: the session lives until reload.
  }
  queryClient.clear()
  listeners.forEach((l) => l())
}

export function clearToken() {
  try {
    localStorage.removeItem(KEY)
  } catch {
    // ignore
  }
  queryClient.clear()
  listeners.forEach((l) => l())
}

// A sign-in or sign-out in another tab arrives as a storage event; it changes
// the session here too, so the cache goes with it.
function onStorage(e: StorageEvent) {
  if (e.key === KEY || e.key === null) queryClient.clear()
}

function subscribe(l: () => void) {
  listeners.add(l)
  window.addEventListener('storage', onStorage)
  window.addEventListener('storage', l)
  return () => {
    listeners.delete(l)
    window.removeEventListener('storage', onStorage)
    window.removeEventListener('storage', l)
  }
}

export function useSession() {
  const token = useSyncExternalStore(subscribe, readToken, () => null)
  return { token, signOut: clearToken }
}
